import {
  Border1,
  ColorBackgroundBodyDark,
  ColorBaseWhite,
  SizeBorder1,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import {
  PaperElevations,
  paperElevationStyles,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import {
  DEFAULT_TYPOGRAPHY_VARIANT,
  Typography,
  TypographyVariants,
} from '@snowball-tech/fractal/dist/src/components/Typography'
import { Themes } from '@snowball-tech/fractal/dist/src/constants'

import {
  type AllHTMLAttributes,
  type CSSProperties,
  type ForwardedRef,
  type ReactNode,
  Children,
  cloneElement,
  forwardRef,
  Fragment,
  isValidElement,
} from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNumber from 'lodash/fp/isNumber'
import omit from 'lodash/fp/omit'

import type { ChildrenProps } from '../types'

import useLogger from '../hooks/useLogger'
import { asPx, classNameToLegacyStyle } from '../styles/helpers'

type Props = {
  autoWidth?: boolean
  cellStyle?: Array<CSSProperties>
  center?: boolean
  col?: boolean
  debug?: boolean
  defaultCellStyle?: CSSProperties
  defaultRowStyle?: CSSProperties
  elevation?: '0' | PaperElevations
  gap?: number
  importantWidth?: boolean
  rowStyles?: Array<CSSProperties>
  theme?: Themes
  variant?: `${TypographyVariants}`
} & AllHTMLAttributes<HTMLTableElement> &
  ChildrenProps

const LegacyTable = forwardRef<HTMLTableElement, Props>(
  (
    {
      autoWidth = false,
      cellStyle,
      center = false,
      children,
      col = false,
      defaultCellStyle,
      defaultRowStyle,
      elevation = '0',
      gap,
      importantWidth = true,
      rowStyles,
      theme,
      variant = DEFAULT_TYPOGRAPHY_VARIANT,
      ...props
    }: Props,
    ref: ForwardedRef<HTMLTableElement>,
  ) => {
    const logger = useLogger()

    const toLegacy = (child: ReactNode) => {
      if (isValidElement(child)) {
        const newChildStyle = classNameToLegacyStyle(
          child.props.className,
          child.props.style,
          logger,
        )

        const classNames = (child.props.className as string)?.split(' ')
        const cleanedClassNames = classNames?.filter((className) => {
          if (
            className.startsWith('preserve-legacy-') ||
            className.startsWith('fractal-')
          ) {
            return true
          }

          return false
        })

        return cloneElement(child, {
          ...child.props,
          className: cleanedClassNames
            ?.join(' ')
            .replaceAll('preserve-legacy-', ''),
          style: newChildStyle,
        })
      }

      return child
    }

    const actualChildren =
      Children.count(children) === 1 &&
      isValidElement(children) &&
      children.type === Fragment
        ? (children.props.children as ReactNode)
        : children

    const filteredChildren = Children.toArray(actualChildren).filter(Boolean)
    const childrenCount = Children.count(filteredChildren)

    const actualTheme = theme || Themes.Light

    const elevationStyles: Record<
      Themes,
      Record<typeof elevation, CSSProperties>
    > = {
      [Themes.Light]: {
        '0': {},
        ...paperElevationStyles[Themes.Light],
      },

      [Themes.Dark]: {
        '0': {},
        ...paperElevationStyles[Themes.Dark],
      },
    }

    return (
      <Typography
        ref={(node) => {
          if (node) {
            node.style.setProperty(
              'width',
              autoWidth
                ? 'auto'
                : Object.hasOwn(props.style ?? {}, 'width')
                  ? isNumber(props.style?.width)
                    ? (asPx(props.style?.width, { withUnit: true }) as string)
                    : (props.style?.width ?? '')
                  : '100%',
              importantWidth ? 'important' : undefined,
            )
          }

          if (isFunction(ref)) {
            ref(node as HTMLTableElement)
          }
        }}
        element="table"
        inlineStyle
        style={{
          backgroundColor: isEmpty(theme)
            ? 'transparent'
            : theme === Themes.Light
              ? ColorBaseWhite
              : ColorBackgroundBodyDark,
          border: elevation === '0' ? 0 : Border1,
          borderCollapse: 'separate',
          borderSpacing: 0,
          borderWidth:
            elevation === '0' || props.style?.border === 0
              ? 0
              : asPx(SizeBorder1, { withUnit: true }),
          maxWidth: '100%',
          tableLayout: 'fixed',
          width: '100%',
          ...elevationStyles[actualTheme][elevation],
          // Box shadow is not supported in most mail clients, so enforce it
          // completely off.
          boxShadow: 'none',
          ...(center ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
          ...props.style,
        }}
        variant={variant}
        {...omit(['style'], props)}
      >
        <tbody>
          {col ? (
            Children.map(filteredChildren, (child, index) => (
              <tr style={{ ...defaultRowStyle, ...rowStyles?.[index] }}>
                <td
                  style={{
                    padding:
                      index < childrenCount - 1 && isNumber(gap) && gap > 0
                        ? `0 0 ${gap}px 0`
                        : 0,
                    ...defaultCellStyle,
                    ...cellStyle?.[index],
                  }}
                >
                  {toLegacy(child)}
                </td>
              </tr>
            ))
          ) : (
            <tr style={{ ...defaultRowStyle, ...rowStyles?.[0] }}>
              {Children.map(filteredChildren, (child, index) => (
                <td
                  style={{
                    padding:
                      index < childrenCount - 1 && isNumber(gap) && gap > 0
                        ? `0 ${gap}px 0 0`
                        : 0,
                    width: `${100 / childrenCount}%`,
                    ...defaultCellStyle,
                    ...cellStyle?.[index],
                  }}
                >
                  {toLegacy(child)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </Typography>
    )
  },
)
LegacyTable.displayName = 'LegacyTable'

export default LegacyTable
